import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Flex, Form } from "@nubeteck/components";
import { Input, Layout, Table, DatePicker, InputNumber, Select, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FormInstance } from "antd/lib/form";
import { toastErrorStyle, toastSuccessStyle } from "src/constants";
import dayjs, { Dayjs } from "dayjs";
import { useGetAdminPeriodsQuery } from "src/services";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";
import {
  useGetZeroWeekDiscountsQuery,
  useInsertZeroWeekDiscountMutation,
  useUpdateZeroWeekDiscountMutation,
  useDeleteZeroWeekDiscountMutation,
} from "src/services/zeroWeekDiscountServices";
import { ArrayUtils } from "@nubeteck/utils";

const { Content } = Layout;
const SCREEN_NAME = "PantallaZeroWeekDiscount";

interface IZeroWeekDiscount {
  id: number;
  fecha: Dayjs;
  porcentaje: number;
  anio: number;
  periodo: string;
  status: string;
}

const ZeroWeekDiscount: React.FC = () => {
  const [form] = Form.useForm<IZeroWeekDiscount>();
  const [discountId, setDiscountId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anio, setAnio] = React.useState(new Date().getFullYear());
  const [filtro, setFiltro] = React.useState<string>("");
  const { data: periodos } = useGetAdminPeriodsQuery(anio);
  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const [translatedData, setTranslatedData] = React.useState<{
    estados: { [key: string]: string };
  }>({
    estados: {},
  });

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const formRef = React.useRef<FormInstance>(null);

  const { data, isLoading, refetch } = useGetZeroWeekDiscountsQuery(undefined);
  const [
    insertDiscount,
    {
      isLoading: isLoadingCreate,
      isSuccess: isSuccessCreate,
      isError: isErrorCreate,
      error: errorCreate,
    },
  ] = useInsertZeroWeekDiscountMutation();
  const [
    updateDiscount,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      error: errorUpdate,
    },
  ] = useUpdateZeroWeekDiscountMutation();
  const [
    deleteDiscount,
    {
      isLoading: isLoadingDelete,
      isSuccess: isSuccessDelete,
      isError: isErrorDelete,

      error: errorDelete,
    },
  ] = useDeleteZeroWeekDiscountMutation();
  const periodosOptions = ArrayUtils.selectLabelValue(periodos?.data ?? [], "nombre", "nombre");

  React.useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isSuccessDelete) {
      toast.success(settingsPage?.success_operation_message || "Operación realizada con éxito", toastSuccessStyle);
      form.resetFields();
      setDiscountId(0);
      setDrawerOpen(false);
      refetch();
    }
    if (isErrorCreate) {
      toast.error(
        errorCreate?.data?.message ?? JSON.parse(errorCreate?.data)?.message,
        toastErrorStyle,
      );
    }
    if (isErrorUpdate) {
      toast.error(
        errorUpdate?.data?.message ?? JSON.parse(errorUpdate?.data)?.message,
        toastErrorStyle,
      );
    }
    if (isErrorDelete) {
      toast.error(
        errorDelete?.data?.message ?? JSON.parse(errorDelete?.data)?.message,
        toastErrorStyle,
      );
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isSuccessDelete,
    isErrorCreate,
    isErrorUpdate,
    isErrorDelete,
    form,
    refetch,
    errorCreate,
    errorUpdate,
    errorDelete,
  ]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;
      if (!hasData) {
        setTranslatedData({ estados: {} });
        return;
      }

      const estadosTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      data.data.forEach((record: IZeroWeekDiscount) => {
        if (record.status && !estadosTranslations.hasOwnProperty(record.status)) {
          estadosTranslations[record.status] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.status, locale).then((translated) => {
              estadosTranslations[record.status] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);
      setTranslatedData({
        estados: estadosTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [data?.data, locale]);

  const onClickEditar = React.useCallback(
    (record: IZeroWeekDiscount) => {
      setDiscountId(record.id);
      form.setFieldsValue({
        ...record,
        fecha: dayjs(record.fecha),
      });
      setDrawerOpen(true);
    },
    [form],
  );

  const submit = (values: any) => {
    // const localTime = values.fecha; // Esto es un objeto Day.js local (e.g., 00:00:00 GMT-4)

    // // Convierte el objeto Day.js a una cadena ISO 8601 en formato UTC (04:00:00Z)
    // const timeToStore = localTime.format("YYYY-MM-DD HH:mm:ss");
    // console.log(timeToStore, "klk");

    const payload = {
      ...values,
      fecha: values.fecha.format("YYYY-MM-DDTHH:mm:ss"),
    };
    console.log(payload, "test");
    if (discountId !== 0) {
      updateDiscount({ id: discountId, ...payload });
    } else {
      insertDiscount(payload);
    }
  };

  const columns = React.useMemo<ColumnsType<IZeroWeekDiscount>>(() => {
    const onDelete = (id: number) => {
      deleteDiscount(id);
    };

    return [
      {
        title: settingsPage?.zero_week_table[0]?.label || "Fecha",
        dataIndex: "fecha",
        render: (val) => dayjs(val).format("DD/MM/YYYY HH:mm:ss"),
      },
      {
        title: settingsPage?.zero_week_table[1]?.label || "Porcentaje",
        dataIndex: "porcentaje",
        render: (val) => `${val}%`,
      },
      {
        title: settingsPage?.zero_week_table[2]?.label || "Año",
        dataIndex: "anio",
      },
      {
        title: settingsPage?.zero_week_table[3]?.label || "Periodo",
        dataIndex: "periodo",
      },
      {
        title: settingsPage?.zero_week_table[4]?.label || "Estado",
        dataIndex: "status",
        render: (text: string) => translatedData.estados[text] || text,
      },
      {
        title: settingsPage?.zero_week_table[5]?.label || "Acciones",
        dataIndex: "actions",
        render: (_, record) => (
          <Flex style={{ gap: "10px" }}>
            <Button size="small" onClick={() => onClickEditar(record)}>
              {settingsPage?.zero_week_detail.edit_button || "Editar"}
            </Button>
            <Button
              size="small"
              danger
              onClick={() => onDelete(record.id)}
              loading={isLoadingDelete}
            >
              {settingsPage?.zero_week_detail.delete_button || "Eliminar"}
            </Button>
          </Flex>
        ),
      },
    ];
  }, [onClickEditar, isLoadingDelete, deleteDiscount, settingsPage, translatedData]);

  const filteredData = data?.data?.filter((d: any) =>
    d.periodo.toLowerCase().includes(filtro.toLowerCase()),
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between", marginBottom: "16px" }}>
              <Skeleton.Input active size="large" style={{ width: 300 }} />
              <Flex style={{ gap: "15px" }}>
                <Skeleton.Input active size="default" style={{ width: 200, height: 32 }} />
                <Skeleton.Button active size="default" style={{ width: 150 }} />
              </Flex>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 5 }} />
            </Card>
          </>
        ) : (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between" }}>
              <LargeHeadingStyled>{settingsPage?.zero_week_detail.title || "Descuentos semana cero"}</LargeHeadingStyled>
              <Flex style={{ gap: "15px" }}>
                <Form.Item label={settingsPage?.zero_week_detail.search_label || "Búsqueda por periodo"}>
                  <Input
                    type="text"
                    size="small"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    placeholder={settingsPage?.zero_week_detail.search_input || "Periodo"}
                    style={{ height: "35px", marginRight: "15px" }}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  onClick={() => {
                    setDrawerOpen(true);
                    form.resetFields();
                  }}
                >
                  {settingsPage?.zero_week_detail.new_descount_button || "Nuevo descuento"}
                </Button>
              </Flex>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<IZeroWeekDiscount>
                loading={isLoading || isLoadingCreate || isLoadingUpdate}
                columns={columns}
                dataSource={filteredData}
                rowKey={(v) => v.id}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
            </Card>
          </>
        )}
        <Drawer
          placement="right"
          title={discountId !== 0 ? settingsPage?.zero_week_detail.edit_discount_title || "Editar descuento" : settingsPage?.zero_week_detail.new_descount_button || "Nuevo descuento"}
          open={drawerOpen}
          onClose={() => {
            setDiscountId(0);
            setDrawerOpen(false);
          }}
        >
          <Form
            form={form}
            layout="vertical"
            ref={formRef}
            onFinish={submit}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <Form.Item
              name="fecha"
              label={settingsPage?.zero_week_detail.date_label || "Fecha"}
              rules={[{ required: true, message: settingsPage?.required_field_message || "Este campo es requerido" }]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm:ss" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="porcentaje"
              label={settingsPage?.zero_week_detail.percentage_label || "Porcentaje"}
              rules={[{ required: true, message: settingsPage?.required_field_message || "Este campo es requerido" }]}
            >
              <InputNumber id="input-normal" min={0} max={100} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="anio"
              label={settingsPage?.zero_week_detail.year_label || "Año"}
              rules={[{ required: true, message: settingsPage?.required_field_message || "Este campo es requerido" }]}
            >
              <InputNumber
                min={new Date().getFullYear()}
                style={{ width: "100%", textAlign: "start" }}
                id="input-normal"
                onChange={(val) => setAnio(val ?? new Date().getFullYear())}
              />
            </Form.Item>
            <Form.Item
              name="periodo"
              label={settingsPage?.zero_week_detail.term_label || "Periodo"}
              rules={[{ required: true, message: settingsPage?.required_field_message || "Este campo es requerido" }]}
            >
              <Select options={periodosOptions} placeholder={settingsPage?.zero_week_detail.select_option || "Seleccione"} />
            </Form.Item>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              style={{ alignSelf: "flex-end" }}
              loading={isLoadingCreate || isLoadingUpdate}
            >
              {settingsPage?.zero_week_detail.save_button || "Guardar"}
            </Button>
          </Form>
        </Drawer>
      </Content>
    </Flex>
  );
};

export default ZeroWeekDiscount;
